import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { QRCodeSVG } from 'qrcode.react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Globe, Clock, MapPin, Phone, Mail, MessageCircle, Package, ArrowLeft, FileDown, X, ShoppingCart, Minus, Plus, Trash2, MessageSquare } from 'lucide-react';
import { toast } from 'sonner';
import ChatButton, { openChat } from '@/components/ChatButton';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PLACEHOLDER_COVER = 'linear-gradient(135deg, #059669 0%, #10b981 50%, #34d399 100%)';

const SellerProfile = () => {
  const { sellerId } = useParams();
  const navigate = useNavigate();
  const { user, getAuthHeader } = useAuth();
  const [profile, setProfile] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('__all__');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [downloadingDoc, setDownloadingDoc] = useState(false);
  // Cart is stored in localStorage keyed by seller id so it survives reloads
  const cartKey = `cart:${sellerId}`;
  const [cart, setCart] = useState(() => {
    try { return JSON.parse(localStorage.getItem(cartKey) || '{}'); } catch { return {}; }
  });
  const [cartOpen, setCartOpen] = useState(false);
  const [checkoutResult, setCheckoutResult] = useState(null);
  const [checkingOut, setCheckingOut] = useState(false);
  const [deliveryChoice, setDeliveryChoice] = useState('pickup');

  useEffect(() => {
    localStorage.setItem(cartKey, JSON.stringify(cart));
    // Persist a light-weight meta record so the buyer's dashboard can list
    // all their carts across sellers without extra API calls.
    const totalQty = Object.values(cart).reduce((s, q) => s + (q || 0), 0);
    if (totalQty > 0 && profile) {
      localStorage.setItem(`cart-meta:${sellerId}`, JSON.stringify({
        business_name: profile.business_name,
        logo_url: profile.logo_url || null,
        updated_at: new Date().toISOString(),
      }));
    } else {
      localStorage.removeItem(`cart-meta:${sellerId}`);
    }
  }, [cart, cartKey, sellerId, profile]);

  const isBuyer = user && (user.role === 'restaurant' || user.role === 'shop');
  const cartItemsCount = Object.values(cart).reduce((sum, q) => sum + (q || 0), 0);

  const addToCart = (item, qty = 1) => {
    setCart((c) => ({ ...c, [item.id]: (c[item.id] || 0) + qty }));
    toast.success(`${item.product} añadido al carrito`);
  };
  const setCartQty = (itemId, qty) => {
    setCart((c) => {
      const next = { ...c };
      if (qty <= 0) delete next[itemId];
      else next[itemId] = qty;
      return next;
    });
  };
  const clearCart = () => setCart({});

  const cartLines = useMemo(() => {
    if (!profile) return [];
    return Object.entries(cart)
      .map(([id, qty]) => {
        const item = profile.catalog.find((c) => c.id === id);
        return item ? { ...item, qty } : null;
      })
      .filter(Boolean);
  }, [cart, profile]);

  const handleCheckout = async () => {
    if (!user) {
      toast.error('Inicia sesión para comprar');
      navigate('/login');
      return;
    }
    if (!isBuyer) {
      toast.error('Solo los compradores pueden hacer pedidos');
      return;
    }
    if (cartLines.length === 0) return;
    setCheckingOut(true);
    try {
      const res = await axios.post(
        `${API}/catalog/checkout`,
        {
          seller_id: sellerId,
          items: cartLines.map((l) => ({ catalog_item_id: l.id, quantity: l.qty })),
          delivery_mode: profile?.catalog_delivery_mode === 'either' ? deliveryChoice : (profile?.catalog_delivery_mode || null),
        },
        { headers: { Authorization: getAuthHeader() } }
      );
      setCheckoutResult(res.data);
      clearCart();
      setCartOpen(false);
    } catch (err) {
      if (err.response?.status === 402) {
        toast.error('Este proveedor debe activar su suscripción antes de poder recibir pedidos.');
      } else {
        toast.error(err.response?.data?.detail || 'No se pudo completar la compra');
      }
    } finally {
      setCheckingOut(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    axios
      .get(`${API}/sellers/${sellerId}/profile`)
      .then((res) => setProfile(res.data))
      .catch((err) => setError(err.response?.data?.detail || 'No se pudo cargar el perfil'))
      .finally(() => setLoading(false));
  }, [sellerId]);

  const filteredCatalog = useMemo(() => {
    if (!profile) return [];
    if (activeCategory === '__all__') return profile.catalog;
    if (activeCategory === '__none__') return profile.catalog.filter(it => !it.category);
    return profile.catalog.filter(it => (it.category || '').trim() === activeCategory);
  }, [profile, activeCategory]);

  const handleDownloadPdf = async () => {
    setDownloadingDoc(true);
    try {
      const res = await axios.get(`${API}/sellers/${sellerId}/catalog-document`);
      // Trigger download from the returned data-URL
      const a = document.createElement('a');
      a.href = res.data.data_url;
      a.download = res.data.filename || 'catalogo.pdf';
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (_) {
      // silently ignore — download unavailable
    } finally {
      setDownloadingDoc(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600" />
      </div>
    );
  }
  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <div className="max-w-md w-full bg-white border border-slate-200 rounded-lg p-6 text-center">
          <p className="text-slate-800 font-semibold mb-2">Perfil no disponible</p>
          <p className="text-sm text-slate-500 mb-4">{error}</p>
          <Button onClick={() => navigate(-1)} variant="outline">Volver</Button>
        </div>
      </div>
    );
  }

  const whatsappHref = profile.whatsapp
    ? `https://wa.me/${(profile.whatsapp || '').replace(/[^\d+]/g, '')}`
    : null;

  const categoriesForFilter = ['__all__', ...(profile.categories || [])];
  const hasUncategorised = profile.catalog?.some(it => !it.category);
  if (hasUncategorised) categoriesForFilter.push('__none__');

  return (
    <div className="min-h-screen bg-slate-50 pb-16" data-testid="seller-profile-page">
      {/* Cover */}
      <div className="relative w-full h-40 sm:h-56" style={{ background: profile.cover_url ? undefined : PLACEHOLDER_COVER }}>
        {profile.cover_url && (
          <img src={profile.cover_url} alt="Portada" className="absolute inset-0 w-full h-full object-cover" data-testid="seller-cover" />
        )}
        <button
          onClick={() => navigate(-1)}
          className="absolute top-4 left-4 bg-white/90 backdrop-blur rounded-full p-2 hover:bg-white transition"
          data-testid="seller-profile-back"
        >
          <ArrowLeft size={18} className="text-slate-700" />
        </button>
        {user && isBuyer && (
          <div className="absolute top-3 right-4 bg-white/90 backdrop-blur rounded-full px-1">
            <ChatButton />
          </div>
        )}
      </div>

      {/* Header */}
      <div className="max-w-3xl mx-auto px-4 -mt-12 relative">
        <div className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm">
          <div className="flex items-start gap-4">
            <div className="w-20 h-20 rounded-lg bg-emerald-100 border border-emerald-200 overflow-hidden flex items-center justify-center flex-shrink-0">
              {profile.logo_url ? (
                <img src={profile.logo_url} alt="Logo" className="w-full h-full object-cover" data-testid="seller-logo" />
              ) : (
                <Package size={32} className="text-emerald-600" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <h1 className="text-xl sm:text-2xl font-bold text-slate-900" data-testid="seller-business-name">
                {profile.business_name}
              </h1>
              {profile.postal_code && (
                <p className="text-sm text-slate-500 flex items-center gap-1 mt-1">
                  <MapPin size={14} /> CP {profile.postal_code}
                </p>
              )}
              <p className="text-xs text-slate-400 mt-1">
                {profile.role === 'manufacturer' ? 'Fabricante' : 'Proveedor / Distribuidor'}
              </p>
              {profile.rating_avg != null && (
                <p className="text-sm text-amber-700 font-semibold mt-1" data-testid="seller-rating">
                  ★ {profile.rating_avg.toFixed(1)} <span className="text-slate-500 font-normal text-xs">({profile.rating_count} reseña{profile.rating_count === 1 ? '' : 's'})</span>
                </p>
              )}
            </div>
          </div>

          {profile.description && (
            <p className="text-sm text-slate-700 mt-4 leading-relaxed whitespace-pre-line" data-testid="seller-description">
              {profile.description}
            </p>
          )}

          <div className="mt-4 grid gap-2 text-sm text-slate-600">
            {profile.website && (
              <a
                href={profile.website}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 text-emerald-700 hover:underline"
                data-testid="seller-website"
              >
                <Globe size={14} /> {profile.website.replace(/^https?:\/\//, '')}
              </a>
            )}
            {profile.hours && (
              <div className="flex items-start gap-2">
                <Clock size={14} className="mt-0.5 flex-shrink-0" />
                <span className="whitespace-pre-line" data-testid="seller-hours">{profile.hours}</span>
              </div>
            )}
          </div>

          {/* PDF catalog download */}
          {profile.catalog_document && (
            <Button
              onClick={handleDownloadPdf}
              disabled={downloadingDoc}
              variant="outline"
              data-testid="download-catalog-pdf-button"
              className="mt-4 w-full sm:w-auto"
            >
              <FileDown size={16} className="mr-2" />
              {downloadingDoc ? 'Descargando…' : `Descargar catálogo completo (PDF · ${(profile.catalog_document.size_bytes / 1024 / 1024).toFixed(1)} MB)`}
            </Button>
          )}
        </div>

        {/* Contact */}
        <div className="mt-4 bg-white border border-slate-200 rounded-lg p-5 shadow-sm">
          <h2 className="text-sm font-bold text-slate-900 mb-3">Contacto</h2>
          {profile.chat_enabled ? (
            <Button
              data-testid="profile-chat-button"
              onClick={() => {
                if (!user) { toast.error('Inicia sesión para chatear'); navigate('/login'); return; }
                if (!isBuyer) { toast.error('El chat con proveedores es para compradores'); return; }
                openChat({ seller_id: sellerId, context_type: 'profile', context_id: sellerId });
              }}
              className="mb-3 w-full sm:w-auto bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              <MessageSquare size={16} className="mr-2" /> Chatear en la app
            </Button>
          ) : (
            <p className="mb-3 text-xs text-slate-500" data-testid="profile-chat-locked">💬 Chat no disponible: el proveedor no tiene suscripción activa.</p>
          )}
          {profile.contact_unlocked ? (
            <div className="flex flex-wrap gap-2" data-testid="profile-contact-unlocked">
              {profile.phone && (
                <a href={`tel:${profile.phone}`} data-testid="profile-contact-phone" className="inline-flex items-center gap-2 text-sm bg-slate-100 hover:bg-slate-200 text-slate-800 px-3 py-2 rounded-md">
                  <Phone size={14} /> {profile.phone}
                </a>
              )}
              {profile.email && (
                <a href={`mailto:${profile.email}`} data-testid="profile-contact-email" className="inline-flex items-center gap-2 text-sm bg-slate-100 hover:bg-slate-200 text-slate-800 px-3 py-2 rounded-md">
                  <Mail size={14} /> {profile.email}
                </a>
              )}
              {whatsappHref && (
                <a href={whatsappHref} target="_blank" rel="noreferrer" data-testid="profile-contact-whatsapp" className="inline-flex items-center gap-2 text-sm bg-emerald-100 hover:bg-emerald-200 text-emerald-800 px-3 py-2 rounded-md font-medium">
                  <MessageCircle size={14} /> WhatsApp
                </a>
              )}
            </div>
          ) : (
            <div data-testid="profile-contact-locked" className="text-xs bg-amber-50 border border-amber-200 text-amber-900 rounded p-3">
              🔒 Los datos de contacto se revelarán cuando este proveedor active su suscripción.
            </div>
          )}
        </div>

        {/* Recent reviews */}
        {profile.recent_reviews && profile.recent_reviews.length > 0 && (
          <div className="mt-4 bg-white border border-slate-200 rounded-lg p-5 shadow-sm" data-testid="seller-reviews-block">
            <h2 className="text-sm font-bold text-slate-900 mb-3">
              Reseñas recientes {profile.rating_avg != null && <span className="text-amber-700 font-semibold">· ★ {profile.rating_avg.toFixed(1)}</span>}
            </h2>
            <ul className="space-y-3">
              {profile.recent_reviews.map((r, i) => (
                <li key={i} className="border-b border-slate-100 last:border-0 pb-3 last:pb-0" data-testid={`seller-review-${i}`}>
                  <p className="text-amber-500 text-sm">{'★'.repeat(r.rating)}<span className="text-slate-300">{'★'.repeat(5 - r.rating)}</span></p>
                  {r.comment && <p className="text-sm text-slate-700 mt-1 italic">"{r.comment}"</p>}
                  {r.created_at && <p className="text-[11px] text-slate-400 mt-1">{new Date(r.created_at).toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' })}</p>}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Catalog */}
        <div className="mt-4 bg-white border border-slate-200 rounded-lg p-5 shadow-sm">
          <h2 className="text-sm font-bold text-slate-900 mb-3">
            Catálogo{profile.catalog_count > 0 ? ` (${profile.catalog_count})` : ''}
          </h2>

          {/* Category filter chips */}
          {categoriesForFilter.length > 1 && (
            <div className="flex flex-wrap gap-2 mb-4" data-testid="category-filter-chips">
              {categoriesForFilter.map((cat) => {
                const label = cat === '__all__' ? 'Todas' : cat === '__none__' ? 'Sin categoría' : cat;
                const active = activeCategory === cat;
                return (
                  <button
                    key={cat}
                    type="button"
                    data-testid={`category-chip-${cat === '__all__' ? 'all' : cat === '__none__' ? 'none' : cat}`}
                    onClick={() => setActiveCategory(cat)}
                    className={`text-xs px-3 py-1.5 rounded-full border transition ${
                      active
                        ? 'bg-emerald-600 border-emerald-600 text-white'
                        : 'bg-white border-slate-200 text-slate-700 hover:border-slate-300'
                    }`}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          )}

          {filteredCatalog.length === 0 ? (
            <p className="text-sm text-slate-500">
              {profile.catalog_count === 0 ? 'Este proveedor aún no tiene productos publicados.' : 'No hay productos en esta categoría.'}
            </p>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="seller-catalog-grid">
              {filteredCatalog.map((it) => (
                <button
                  key={it.id}
                  type="button"
                  onClick={() => setSelectedProduct(it)}
                  data-testid={`seller-catalog-item-${it.id}`}
                  className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden text-left hover:shadow-md hover:border-emerald-300 transition-all duration-200"
                >
                  <div className="aspect-square bg-slate-50 relative">
                    {it.image_url ? (
                      <img src={it.image_url} alt={it.product} className="absolute inset-0 w-full h-full object-cover" />
                    ) : (
                      <div className="absolute inset-0 flex items-center justify-center text-slate-300">
                        <Package size={36} />
                      </div>
                    )}
                    {it.category && (
                      <span className="absolute top-1 left-1 text-[10px] bg-white/90 text-slate-700 px-1.5 py-0.5 rounded font-medium">
                        {it.category}
                      </span>
                    )}
                  </div>
                  <div className="p-2">
                    <p className="text-sm font-semibold text-slate-900 line-clamp-2 leading-snug">{it.product}</p>
                    <p className="text-xs text-slate-500">{it.format}</p>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Product detail dialog */}
      <Dialog open={!!selectedProduct} onOpenChange={(o) => !o && setSelectedProduct(null)}>
        <DialogContent data-testid="product-detail-dialog" className="max-w-lg max-h-[90vh] overflow-y-auto p-0">
          {selectedProduct && (
            <>
              <div className="relative aspect-square bg-slate-50">
                {selectedProduct.image_url ? (
                  <img src={selectedProduct.image_url} alt={selectedProduct.product} className="absolute inset-0 w-full h-full object-cover" data-testid="product-detail-image" />
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center text-slate-300">
                    <Package size={80} />
                  </div>
                )}
                <button
                  onClick={() => setSelectedProduct(null)}
                  className="absolute top-3 right-3 bg-white/90 hover:bg-white rounded-full p-2"
                  data-testid="product-detail-close"
                >
                  <X size={16} className="text-slate-700" />
                </button>
              </div>
              <div className="p-5 space-y-3">
                <DialogHeader className="text-left space-y-1">
                  {selectedProduct.category && (
                    <span className="inline-block text-[11px] bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded font-medium w-fit">
                      {selectedProduct.category}
                    </span>
                  )}
                  <DialogTitle data-testid="product-detail-title">{selectedProduct.product}</DialogTitle>
                  <DialogDescription>Formato: {selectedProduct.format}</DialogDescription>
                </DialogHeader>
                {selectedProduct.description && (
                  <div>
                    <p className="text-xs font-medium text-slate-700 mb-1">Descripción</p>
                    <p className="text-sm text-slate-600 whitespace-pre-line leading-relaxed" data-testid="product-detail-description">
                      {selectedProduct.description}
                    </p>
                  </div>
                )}
                <p className="text-xs text-slate-400">
                  Añadido el {new Date(selectedProduct.created_at).toLocaleDateString('es-ES')}
                </p>
                {profile.contact_unlocked && (
                  <Button
                    onClick={() => { addToCart(selectedProduct); setSelectedProduct(null); }}
                    data-testid="product-add-to-cart-button"
                    className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
                  >
                    <ShoppingCart size={16} className="mr-2" /> Añadir al carrito
                  </Button>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Floating cart bar */}
      {cartItemsCount > 0 && profile.contact_unlocked && (
        <div className="fixed bottom-0 inset-x-0 z-40 border-t border-slate-200 bg-white/95 backdrop-blur shadow-lg" data-testid="cart-floating-bar">
          <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-3">
            <div className="flex-1">
              <p className="text-sm font-semibold text-slate-900">
                {cartItemsCount} artículo{cartItemsCount === 1 ? '' : 's'} en el carrito
              </p>
              <p className="text-xs text-slate-500">Sin coste — el proveedor te contactará</p>
            </div>
            <Button
              onClick={() => setCartOpen(true)}
              data-testid="open-cart-button"
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              <ShoppingCart size={16} className="mr-2" /> Ver carrito
            </Button>
          </div>
        </div>
      )}

      {/* Cart dialog */}
      <Dialog open={cartOpen} onOpenChange={setCartOpen}>
        <DialogContent data-testid="cart-dialog" className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Tu carrito</DialogTitle>
            <DialogDescription>
              Revisa los productos antes de solicitar el pedido a {profile.business_name}.
            </DialogDescription>
          </DialogHeader>
          {cartLines.length === 0 ? (
            <p className="text-sm text-slate-500 py-6 text-center">El carrito está vacío.</p>
          ) : (
            <div className="space-y-2 py-2">
              {cartLines.map((line) => (
                <div key={line.id} className="flex items-center gap-2 border border-slate-100 rounded-lg p-2" data-testid={`cart-line-${line.id}`}>
                  <div className="w-12 h-12 rounded bg-slate-50 overflow-hidden flex items-center justify-center flex-shrink-0">
                    {line.image_url ? (
                      <img src={line.image_url} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <Package size={20} className="text-slate-300" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-slate-900 truncate">{line.product}</p>
                    <p className="text-xs text-slate-500">{line.format}</p>
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => setCartQty(line.id, line.qty - 1)}
                      data-testid={`cart-decrement-${line.id}`}
                      className="w-7 h-7 rounded border border-slate-200 hover:bg-slate-50 flex items-center justify-center"
                    >
                      <Minus size={14} />
                    </button>
                    <span className="w-8 text-center text-sm font-semibold" data-testid={`cart-qty-${line.id}`}>{line.qty}</span>
                    <button
                      onClick={() => setCartQty(line.id, line.qty + 1)}
                      data-testid={`cart-increment-${line.id}`}
                      className="w-7 h-7 rounded border border-slate-200 hover:bg-slate-50 flex items-center justify-center"
                    >
                      <Plus size={14} />
                    </button>
                  </div>
                  <button
                    onClick={() => setCartQty(line.id, 0)}
                    data-testid={`cart-remove-${line.id}`}
                    className="text-red-500 hover:text-red-700 p-1"
                    aria-label="Quitar del carrito"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
              <div className="text-xs text-slate-500 pt-2">
                Total artículos: <strong>{cartItemsCount}</strong> · Sin coste para el comprador.
              </div>
              <div className="pt-2">
                <p className="text-xs font-semibold text-slate-700 mb-1">Entrega</p>
                {profile.catalog_delivery_mode === 'either' ? (
                  <div className="grid grid-cols-2 gap-2" data-testid="cart-delivery-choice">
                    <button type="button" data-testid="cart-delivery-delivery" onClick={() => setDeliveryChoice('delivery')} className={`p-2 rounded-lg border-2 text-left text-xs transition-colors ${deliveryChoice === 'delivery' ? 'border-emerald-600 bg-emerald-50' : 'border-slate-200 hover:border-slate-300'}`}>
                      <p className="font-bold text-slate-900">🚚 Entrega a domicilio</p>
                      <p className="text-slate-500">{profile.catalog_delivery_fee > 0 ? `+ €${Number(profile.catalog_delivery_fee).toFixed(2)} transporte` : 'Sin coste de transporte'}</p>
                    </button>
                    <button type="button" data-testid="cart-delivery-pickup" onClick={() => setDeliveryChoice('pickup')} className={`p-2 rounded-lg border-2 text-left text-xs transition-colors ${deliveryChoice === 'pickup' ? 'border-emerald-600 bg-emerald-50' : 'border-slate-200 hover:border-slate-300'}`}>
                      <p className="font-bold text-slate-900">🏬 Recogida</p>
                      <p className="text-slate-500">Recoges en el proveedor</p>
                    </button>
                  </div>
                ) : (
                  <p className="text-xs text-slate-700 bg-slate-50 border border-slate-200 rounded-lg p-2" data-testid="cart-delivery-fixed">
                    🚚 {profile.catalog_delivery_mode === 'delivery' ? 'Entrega a domicilio' : 'Recogida en el proveedor'}
                    {profile.catalog_delivery_mode === 'delivery' && profile.catalog_delivery_fee > 0 && ` · + €${Number(profile.catalog_delivery_fee).toFixed(2)} transporte`}
                  </p>
                )}
              </div>
            </div>
          )}
          <div className="flex gap-3">
            <Button variant="outline" onClick={() => setCartOpen(false)} className="flex-1">Seguir mirando</Button>
            <Button
              onClick={handleCheckout}
              disabled={cartLines.length === 0 || checkingOut}
              data-testid="checkout-button"
              className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              {checkingOut ? 'Procesando…' : 'Comprar'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Checkout result dialog — QR + supplier contact */}
      <Dialog open={!!checkoutResult} onOpenChange={(o) => !o && setCheckoutResult(null)}>
        <DialogContent data-testid="checkout-result-dialog" className="max-w-md">
          <DialogHeader>
            <DialogTitle>¡Pedido registrado!</DialogTitle>
            <DialogDescription>
              Muestra este código al proveedor para recoger tu pedido.
            </DialogDescription>
          </DialogHeader>
          {checkoutResult && (
            <div className="space-y-3 py-2">
              <div className="bg-white p-4 rounded-lg border border-slate-200 flex flex-col items-center">
                <QRCodeSVG value={checkoutResult.pickup_token} size={180} data-testid="checkout-qr" />
                <p className="mt-2 font-mono font-bold text-lg text-slate-900" data-testid="checkout-token">
                  {checkoutResult.pickup_token}
                </p>
              </div>
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-sm">
                <p className="font-semibold text-slate-900 mb-1">Productos ({checkoutResult.items.length})</p>
                <ul className="space-y-0.5 text-slate-700">
                  {checkoutResult.items.map((it) => (
                    <li key={it.catalog_item_id}>• {it.quantity} × {it.product} <span className="text-slate-500">({it.format})</span></li>
                  ))}
                </ul>
              </div>
              {checkoutResult.delivery_mode && (
                <div className={`rounded-lg p-3 text-sm border ${checkoutResult.delivery_mode === 'delivery' ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200'}`} data-testid="checkout-delivery">
                  <p className="font-semibold text-slate-900">🚚 {checkoutResult.delivery_mode === 'delivery' ? 'Entrega a domicilio' : 'Recogida en el proveedor'}</p>
                  {checkoutResult.delivery_mode === 'delivery' && checkoutResult.delivery_fee > 0 && (
                    <p className="text-xs text-slate-600 mt-0.5">Transporte: €{Number(checkoutResult.delivery_fee).toFixed(2)}</p>
                  )}
                </div>
              )}
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-sm">
                <p className="font-semibold text-slate-900">Contacto del proveedor</p>
                <p className="text-slate-700">{checkoutResult.supplier_name}</p>
                {checkoutResult.supplier_phone && (
                  <p className="text-slate-700">📞 {checkoutResult.supplier_phone}</p>
                )}
                {checkoutResult.supplier_email && (
                  <p className="text-slate-700">✉️ {checkoutResult.supplier_email}</p>
                )}
              </div>
              <p className="text-xs text-slate-500 text-center px-2">
                También podrás verlo en <strong>Matches</strong> del panel hasta que el proveedor lo marque como recogido.
              </p>
              <Button
                onClick={() => setCheckoutResult(null)}
                data-testid="checkout-close-button"
                className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
              >
                Entendido
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SellerProfile;
